import React from 'react';
import { Star, Crown, Lock, Play, Clock, CheckCircle } from 'lucide-react';
import { Book, CollectibleObject } from '../types';
import { Capacitor } from '@capacitor/core';

interface BookCardProps {
  book: Book;
  completed: boolean;
  inProgress?: boolean;
  allObjects: CollectibleObject[];
  onRead: () => void;
  showPrice: boolean;
  isPremium?: boolean;
  viewMode?: 'list' | 'thumbnail' | 'preview-card';
}

const BookCard: React.FC<BookCardProps> = ({
  book,
  completed,
  inProgress = false,
  allObjects,
  onRead,
  showPrice,
  isPremium = false,
  viewMode: propViewMode
}) => {
  const isNative = Capacitor.isNativePlatform();
  // Auto-detect view mode: thumbnail on mobile, list on web
  const viewMode = propViewMode || (isNative ? 'thumbnail' : 'list');

  const bookObjects = allObjects.filter(obj => book.objects?.includes(obj.id));

  const getButtonText = () => {
    if (inProgress) return 'Reprendre';
    if (completed) return 'Relire';
    return 'Lire';
  };

  const getStatusBadge = () => {
    if (inProgress) {
      return (
        <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full">
          <Clock size={12} />
          <span className="text-xs font-semibold">En cours</span>
        </div>
      );
    }
    if (completed) {
      return (
        <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full">
          <CheckCircle size={12} />
          <span className="text-xs font-semibold">Terminé</span>
        </div>
      );
    }
    return null;
  };

  // ===================
  // PREVIEW CARD VIEW (Aperçu gratuit mobile) - 3 cartes rectangulaires paysage
  // ===================
  if (viewMode === 'preview-card') {
    return (
      <div
        onClick={onRead}
        className={`relative bg-white rounded-xl shadow-md overflow-hidden cursor-pointer active:scale-[0.98] transition-transform ${
          completed ? 'ring-2 ring-green-400' : ''
        }`}
      >
        <div className="flex h-28">
          {/* Cover Image - Format paysage */}
          <div className="w-28 flex-shrink-0 bg-gradient-to-br from-blue-200 to-purple-200 relative">
            {book.cover.startsWith('http://') || book.cover.startsWith('https://') || book.cover.startsWith('/') ? (
              <img
                src={book.cover}
                alt={book.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-3xl">{book.cover}</span>
              </div>
            )}

            {/* Status Badge */}
            {(inProgress || completed) && (
              <div className="absolute top-1 left-1">
                {inProgress ? (
                  <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center shadow">
                    <Clock size={10} className="text-white" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shadow">
                    <CheckCircle size={10} className="text-white" />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 p-3 flex flex-col justify-center min-w-0">
            <div className="flex items-start gap-2 mb-1">
              <h3 className="font-bold text-sm text-gray-900 line-clamp-2 flex-1">{book.title}</h3>
              {book.premium && (
                <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                  <Crown size={10} className="text-white" />
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mb-1">{book.category} • {book.pages} p.</p>

            {/* Objets collectibles */}
            {bookObjects.length > 0 && (
              <div className="flex items-center gap-0.5 mb-2">
                {bookObjects.slice(0, 4).map(obj => (
                  <img
                    key={obj.id}
                    src={obj.emoji}
                    alt={obj.name}
                    className="w-4 h-4 object-contain"
                  />
                ))}
                {bookObjects.length > 4 && (
                  <span className="text-xs text-gray-400">+{bookObjects.length - 4}</span>
                )}
              </div>
            )}

            {/* Read button */}
            <button className={`self-start px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
              inProgress
                ? 'bg-orange-500 text-white'
                : completed
                  ? 'bg-blue-500 text-white'
                  : 'bg-green-500 text-white'
            }`}>
              <Play size={12} />
              {inProgress ? 'Reprendre' : completed ? 'Relire' : 'Lire'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===================
  // THUMBNAIL VIEW (Mobile-first) - Format carré, 3 par ligne
  // ===================
  if (viewMode === 'thumbnail') {
    return (
      <div
        onClick={onRead}
        className={`relative bg-white rounded-lg shadow-md overflow-hidden cursor-pointer active:scale-95 transition-transform ${
          completed ? 'ring-2 ring-green-400' : ''
        }`}
      >
        {/* Cover Image - Format carré */}
        <div className="aspect-square bg-gradient-to-br from-blue-200 to-purple-200 relative">
          {book.cover.startsWith('http://') || book.cover.startsWith('https://') || book.cover.startsWith('/') ? (
            <img
              src={book.cover}
              alt={book.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-4xl">{book.cover}</span>
            </div>
          )}

          {/* Status Badge (top left) - compact */}
          {(inProgress || completed) && (
            <div className="absolute top-1 left-1">
              {inProgress ? (
                <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center shadow">
                  <Clock size={10} className="text-white" />
                </div>
              ) : (
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shadow">
                  <CheckCircle size={10} className="text-white" />
                </div>
              )}
            </div>
          )}

          {/* Premium Badge (top right) - compact */}
          {book.premium && (
            <div className="absolute top-1 right-1">
              <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center shadow">
                <Crown size={10} className="text-white" />
              </div>
            </div>
          )}
        </div>

        {/* Title only - compact */}
        <div className="p-2">
          <h3 className="font-semibold text-xs text-gray-900 line-clamp-2 leading-tight">{book.title}</h3>
        </div>

        {/* Locked overlay for premium books */}
        {book.premium && !isPremium && showPrice && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-lg px-2 py-1 shadow-lg flex items-center gap-1">
              <Lock size={12} className="text-orange-500" />
              <span className="font-bold text-orange-500 text-xs">Premium</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===================
  // LIST VIEW (Web)
  // ===================
  return (
    <div className={`bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 ${
      completed ? 'ring-2 ring-green-400' : ''
    }`}>
      <div className="flex h-40">
        {/* Cover Image - Full Height */}
        <div className="w-32 md:w-40 flex-shrink-0 bg-gradient-to-br from-blue-200 to-purple-200 relative">
          {book.cover.startsWith('http://') || book.cover.startsWith('https://') || book.cover.startsWith('/') ? (
            <img
              src={book.cover}
              alt={book.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-5xl">{book.cover}</span>
            </div>
          )}

          {/* Status Badge (left edge, centered vertically) */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2">
            {inProgress && (
              <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center shadow-lg border-2 border-white">
                <Clock size={14} className="text-white" />
              </div>
            )}
            {completed && !inProgress && (
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center shadow-lg border-2 border-white">
                <CheckCircle size={14} className="text-white" />
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 flex flex-col justify-center min-w-0">
          {/* Title Row */}
          <div className="flex items-start gap-2 mb-1">
            <h3 className="text-lg font-bold text-gray-900 line-clamp-1 flex-1">{book.title}</h3>
            {book.premium && (
              <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs flex items-center gap-1 flex-shrink-0">
                <Crown size={10} />
                Premium
              </span>
            )}
          </div>

          {/* Category & Pages */}
          <p className="text-sm text-gray-500 mb-2">{book.category} • {book.pages} pages</p>

          {/* Collectible Objects */}
          {bookObjects.length > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-400 mr-1">Objets :</span>
              {bookObjects.slice(0, 5).map(obj => (
                <img
                  key={obj.id}
                  src={obj.emoji}
                  alt={obj.name}
                  className="w-5 h-5 object-contain"
                  title={obj.name}
                />
              ))}
              {bookObjects.length > 5 && (
                <span className="text-xs text-gray-400">+{bookObjects.length - 5}</span>
              )}
            </div>
          )}
        </div>

        {/* Action Button - Right side, vertically centered */}
        <div className="flex items-center pr-4">
          {book.premium && !isPremium && showPrice ? (
            <button className="bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 flex items-center gap-2 transition-colors shadow-md">
              <Lock size={16} />
              <span className="font-semibold">2.99€</span>
            </button>
          ) : (
            <button
              onClick={onRead}
              className={`py-3 px-5 rounded-full flex items-center gap-2 font-semibold transition-all hover:scale-105 shadow-md ${
                inProgress
                  ? 'bg-orange-500 hover:bg-orange-600 text-white'
                  : completed
                    ? 'bg-blue-500 hover:bg-blue-600 text-white'
                    : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
            >
              <Play size={18} />
              {getButtonText()}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookCard;
